import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '@orisini/ui/context/AuthContext'
import { Button } from '@orisini/ui/components/button'
import { Bagian, GagalMuat } from '../components/Bagian'
import { BagianCara } from '../components/BagianCara'
import { BagianHarga } from '../components/BagianHarga'
import { BagianProduk } from '../components/BagianProduk'
import { BagianTanya } from '../components/BagianTanya'
import { BagianUnduhan } from '../components/BagianUnduhan'
import { PapanMinggu } from '../components/PapanMinggu'
import { SitusFooter } from '../components/SitusFooter'
import { SitusNav } from '../components/SitusNav'
import { rupiah, usePaket, useProduk, useRilis } from '../lib/data'

/**
 * Halaman depan publik orisini.com.
 *
 * Susunannya adalah papan minggu: tujuh hari sungguhan mulai hari ini, satu
 * jalur untuk tiap produk di katalog, dan seluruh bagian di bawahnya memakai
 * garis rambut yang sama sehingga halaman terbaca sebagai satu lembar berbaris.
 *
 * Pengunjung yang sudah punya sesi langsung dibawa ke daftar produknya — cookie
 * berlaku lintas subdomain, jadi orang yang datang dari pinboss.orisini.com
 * tidak perlu masuk lagi di sini.
 */
export default function LandingPage() {
  const { status } = useAuth()

  const { produk, gagal: gagalProduk, ulangi: ulangiProduk } = useProduk()
  const paket = usePaket()
  const rilis = useRilis()

  const [tahunan, setTahunan] = useState(false)

  if (status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <img src="/favicon.svg" alt="" className="size-6 animate-pulse" />
      </div>
    )
  }
  if (status === 'authenticated') return <Navigate to="/app" replace />

  return (
    <div className="min-h-dvh bg-background">
      <SitusNav />

      <main>
        <div className="mx-auto w-full max-w-6xl px-6">
          <div className="grid gap-x-12 gap-y-10 border-b border-border py-16 lg:grid-cols-12 lg:py-20">
            <div className="lg:col-span-7">
              <h1 className="text-balance text-4xl font-semibold tracking-tight sm:text-5xl xl:text-6xl">
                Schedule once. It publishes itself.
              </h1>
              <p className="mt-6 max-w-[62ch] text-pretty text-lg text-muted-foreground">
                Orisini stores your posting schedule, then publishes it from your own computer
                when the time comes. No platform's official API is used, and your logins never
                leave that machine.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg">
                  <Link to="/register">Create a free account</Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="#download">Download agent</a>
                </Button>
              </div>

              <p className="mt-5 text-sm text-muted-foreground">
                First 30 posts free · Windows 10 or newer · Google Chrome required
              </p>
            </div>

            <div className="lg:col-span-5 lg:border-l lg:border-border lg:pl-12">
              <h2 className="text-sm font-medium">Starting from</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                One subscription covers every platform. The limits are on what you use, not on
                which platforms you post to.
              </p>

              <dl className="mt-6 divide-y divide-border border-y border-border">
                {paket.paket.length === 0 ? (
                  <div className="py-4 text-sm text-muted-foreground">Loading plans…</div>
                ) : (
                  paket.paket.map((t) => (
                    <div key={t.code} className="flex items-baseline justify-between gap-4 py-3">
                      <dt className="text-sm">{t.name}</dt>
                      <dd className="text-sm tabular-nums">
                        {t.priceMonthly === 0 ? 'Free' : `${rupiah(t.priceMonthly)}/month`}
                      </dd>
                    </div>
                  ))
                )}
              </dl>

              <a
                href="#pricing"
                className="mt-4 inline-block rounded-sm text-sm text-foreground underline underline-offset-4 decoration-border transition-colors hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Plan and quota details
              </a>
            </div>
          </div>
        </div>

        <Bagian
          id="week"
          judul="The next seven days, one board"
          keterangan="The columns are real dates starting today according to your computer's clock, and each row is one platform. Platforms that aren't supported yet still get their row — so you can see what's usable today, and what isn't."
        >
          {gagalProduk ? (
            <GagalMuat pesan="Couldn't load the product catalog." ulangi={ulangiProduk} />
          ) : (
            <PapanMinggu produk={produk} />
          )}
        </Bagian>

        <BagianProduk produk={produk} />
        <BagianCara />

        <BagianHarga
          paket={paket.paket}
          memuat={paket.memuat}
          gagal={paket.gagal}
          ulangi={paket.ulangi}
          tahunan={tahunan}
          padaGantiPeriode={setTahunan}
        />

        <BagianUnduhan rilis={rilis.rilis} gagal={rilis.gagal} ulangi={rilis.ulangi} />
        <BagianTanya />
      </main>

      <SitusFooter produk={produk} />
    </div>
  )
}
